import { DropdownMenuItem, DropdownMenuSeparator, Outline } from '@ths/ui';

import { ClientPod, useHotelMembershipsQuery } from '../generated/graphql';
import { getContactEmailByPod } from '../utilities/getContactEmailByPod';

export function CompanyMenuItems({
  currentClientId,
  onSelect,
}: {
  currentClientId: string;
  onSelect: (clientId: string) => void;
}) {
  const { data, previousData, loading, error, refetch } =
    useHotelMembershipsQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });
  const cachedData = data ?? previousData;
  const memberships = cachedData?.hotelMemberships ?? [];
  const retry = (
    <>
      <p
        role={cachedData ? 'status' : 'alert'}
        className="px-3 py-2 text-sm text-text-secondary"
      >
        {cachedData
          ? 'Couldn’t refresh companies.'
          : 'Company access could not be loaded.'}
      </p>
      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault();
          void refetch();
        }}
      >
        Try again
      </DropdownMenuItem>
    </>
  );

  if (loading && !cachedData) {
    return (
      <p role="status" className="p-3 text-sm text-text-secondary">
        Loading companies…
      </p>
    );
  }
  if (error && !cachedData) {
    return retry;
  }
  if (memberships.length === 0) {
    return (
      <>
        <p className="px-3 py-2 text-sm text-text-secondary">
          No companies are assigned to your account. Contact THS to request
          access.
        </p>
        <DropdownMenuItem asChild>
          <a href={`mailto:${getContactEmailByPod(ClientPod.Other)}`}>
            Contact THS
          </a>
        </DropdownMenuItem>
        {error && retry}
      </>
    );
  }
  return (
    <>
      {memberships.map((company) => (
        <DropdownMenuItem
          key={company.clientId}
          textValue={company.clientName}
          aria-current={
            company.clientId === currentClientId ? 'true' : undefined
          }
          className="min-h-10 gap-3 px-3 py-2"
          onSelect={() => onSelect(company.clientId)}
        >
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            <span className="block font-medium">{company.clientName}</span>
            {company.isInactive && (
              <span className="block text-xs text-text-secondary">
                Inactive · Existing reservations only
              </span>
            )}
          </span>
          {company.clientId === currentClientId && (
            <Outline.CheckIcon
              className="size-4 shrink-0 text-accent"
              aria-hidden="true"
            />
          )}
        </DropdownMenuItem>
      ))}
      {error && (
        <>
          <DropdownMenuSeparator />
          {retry}
        </>
      )}
    </>
  );
}
